<script setup>
import i18n from '@/plugins/i18n'
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useDetectDevice } from '@/composables/device'
import { useRoute, useRouter } from 'vue-router'
import { useNotify } from '@/composables/notify'
import { useWebSiteStore } from '@/stores/website.store'

import ScheduledList from '@/components/scheduled/ScheduledList.vue'
import ScheduledMessageFormDialog from '@/components/scheduled/ScheduledMessageFormDialog.vue'
import AgentQrDialog from '@/components/agents/AgentQrDialog.vue'
import LimitsDashboard from '@/components/shared/LimitsDashboard.vue'

const t = i18n.global.t
const { notifyError, notifySuccess } = useNotify()
const route = useRoute()
const router = useRouter()
const { isMobile } = useDetectDevice()
const websiteStore = useWebSiteStore()

const loadingLimits = ref(false)
const loadingMessages = ref(false)

const showCreateDialog = ref(false)
const showEditDialog = ref(false)
const editItemId = ref(null)

const qrBlobUrl = ref(null)
const qrLoading = ref(false)
const qrDialogOpen = ref(false)
const qrAgent = ref(null)

let scheduledPollingTimeout = null
let isPolling = false

const HIGHLIGHT_DURATION_MS = 10000

const computeNextPollingTime = () => {
  if (scheduledPollingTimeout) {
    clearTimeout(scheduledPollingTimeout)
    scheduledPollingTimeout = null
  }
  const next_polling_time_utc = websiteStore.getNextScheduledMessagesPollingTime()
  if (next_polling_time_utc == null) return
  const delay = next_polling_time_utc - Date.now() + 5000
  if (delay < 0) {
    fetchMessagesAsync()
    return
  }

  scheduledPollingTimeout = setTimeout(() => {
    fetchMessagesAsync()
  }, delay)
}

const limits = computed(() => {
  const data = websiteStore.userLimits
  if (data && data.scheduled_messages) {
    return data
  }
  return { scheduled_messages: { current: 0, total: 0 } }
})

const progress = computed(() => {
  if (!limits.value?.scheduled_messages?.total) return 0
  return Math.min(
    100,
    Math.round(
      (limits.value.scheduled_messages.current /
        limits.value.scheduled_messages.total) *
        100
    )
  )
})

const messages = computed(() => {
  if (!websiteStore.scheduledMessages) return []

  const enriched = enrichWithNextRun(websiteStore.scheduledMessages)

  return [...enriched].sort((a, b) => {
    if (a.failure_reason && !b.failure_reason) return -1
    if (!a.failure_reason && b.failure_reason) return 1

    if (!a.nextRun) return 1
    if (!b.nextRun) return -1

    return new Date(a.nextRun) - new Date(b.nextRun)
  })
})

const user_agents = computed(() => websiteStore.agents)

watch(
  () => route.query.create,
  (val) => { showCreateDialog.value = !!val }
)

watch(
  () => route.query.edit,
  (val) => {
    showEditDialog.value = !!val
    editItemId.value = val
  }
)

const openCreateDialog = () => {
  const query = { ...route.query, create: '1' }
  router.push({ query })
}

const closeCreateDialog = () => {
  showCreateDialog.value = false
  setTimeout(() => {
    const query = { ...route.query }
    delete query.create
    router.replace({ query })
  }, 300)
}

const closeEditDialog = () => {
  showEditDialog.value = false
  setTimeout(() => {
    const query = { ...route.query }
    delete query.edit
    router.replace({ query })
    editItemId.value = null
  }, 300)
}

const fetchLimitsAsync = async() => {
  loadingLimits.value = true
  try {
    await websiteStore.fetchUserLimits()
  } finally {
    loadingLimits.value = false
  }
}

const fetchMessagesAsync = async() => {
  loadingMessages.value = true
  if (isPolling) {
    return
  }
  isPolling = true
  try {
    await websiteStore.fetchMessagesAsync()
  } finally {
    loadingMessages.value = false
    isPolling = false
  }
}

const fetchAgentsAsync = async() => {
  try {
    await websiteStore.fetchAgentsAsync()
  } catch (error) {
    notifyError(t('scheduled.notifications.errorFetchingAgents'), error)
  }
}

const fetchSubscriptionsAsync = async() => {
  try {
    await websiteStore.fetchSubscriptions()
  } catch (error) {
    notifyError(t('scheduled.notifications.errorFetchingSubscriptions'), error)
  }
}

const enrichWithNextRun = (items) =>
  items.map(m => {
    const runs = websiteStore.computeNextRun(
      m.active,
      m.crontab_expression,
      m.scheduled_time,
      m.timezone,
      i18n.global.locale.value
    )

    return {
      ...m,
      nextRun: Array.isArray(runs) ? (runs[0] ?? null) : null
    }
  })

const toggleActive = async(msg, val) => {
  try {
    await websiteStore.toggleScheduledMessageActive(msg.id, val)
  } catch (error) {
    if (error.response?.status === 500) {
      notifyError(t('error.serverError'))
      return
    }
    const errors = error.response?.data || {}
    for (const field in errors) {
      if (errors.hasOwnProperty(field)) {
        errors[field].forEach((errorMsg) => {
          notifyError(t(errorMsg))
        })
      }
    }
  }
}

const deleteScheduledMessageAsync = async(msg) => {
  try {
    await websiteStore.deleteScheduledMessageAsync(msg.id)
    notifySuccess(t('scheduled.notifications.deleteSuccess'))
  } catch (error) {
    notifyError(t('scheduled.notifications.errorDeletingMessage'))
  }
}

// Un messaggio appena creato nasce spento e niente lo dice: per una decina di secondi il suo
// toggle pulsa. Lo stato sta in memoria (non in localStorage ne' nella query): ricaricando la
// pagina non pulsa piu', e non pulsa a ogni visita.
const justCreatedId = ref(null)
let justCreatedTimeout = null

const highlightCreated = (id) => {
  if (!id) return
  if (justCreatedTimeout) clearTimeout(justCreatedTimeout)
  justCreatedId.value = id
  justCreatedTimeout = setTimeout(() => { justCreatedId.value = null }, HIGHLIGHT_DURATION_MS)
}

const onCreateSaved = async(newId) => {
  closeCreateDialog()
  await fetchLimitsAsync()
  await fetchMessagesAsync()
  highlightCreated(newId)
}

const onEditSaved = async() => {
  closeEditDialog()
  await fetchLimitsAsync()
  await fetchMessagesAsync()
}

watch(
  () => websiteStore.scheduledMessages,
  () => {
    computeNextPollingTime()
  },
  { deep: true }
)

onMounted(async() => {
  await Promise.all([
    fetchLimitsAsync(),
    fetchMessagesAsync(),
    fetchAgentsAsync(),
    fetchSubscriptionsAsync()
  ])
})

onUnmounted(() => {
  if (scheduledPollingTimeout) {
    clearTimeout(scheduledPollingTimeout)
    scheduledPollingTimeout = null
  }
})
</script>

<template>
  <q-page
    class="q-pa-md"
  >
    <div class="q-mb-md">
      <div class="text-h5 text-weight-bold">
        {{ $t('scheduled.title') }}
      </div>
      <div class="text-subtitle2 text-grey-7">
        {{ $t('scheduled.subtitle') }}
      </div>
    </div>

    <div class="q-mb-md">
      <q-banner
        v-if="limits.scheduled_messages.current >= limits.scheduled_messages.total && limits.scheduled_messages.total > 0"
        dense
        rounded
        inline-actions
        class="bg-warning text-white"
      >
        <div class="text-body2">
          {{ $t('scheduled.notifications.creditExpired') }}
        </div>
      </q-banner>
    </div>

    <!-- <div
      v-if="!websiteStore.hasWarmupService"
    >
      <agent-item-card
        v-if="user_agents.length > 0"
        class="q-mb-md"
        :item="user_agents[0]"
        :max-phone-number-changes="maxPhoneNumberChanges"
        @start="onStart"
        @stop="onStop"
        @show-qr="onShowQr"
        @detail="onAgentDetail"
        @upload-chats="onUploadChats"
      />

      <div
        v-else
        class="q-mb-md"
      >
        <q-form
          class="row q-col-gutter-sm items-center no-wrap"
          @submit.prevent="createAgentAsync({ associated_phone_number: agentNumber })"
        >
          <div class="col">
            <q-tel-input
              v-model="agentNumber"
              :label="t('scheduled.agent.agentNumber')"
              :locales="locale"
              outlined
              dense
            />
          </div>

          <div class="col-auto">
            <q-btn
              type="submit"
              color="primary"
              :label="t('common.save')"
              unelevated
              style="height: 40px"
            />
          </div>
        </q-form>
      </div>
      <q-dialog v-model="showAgentEditDialog">
        <q-card>
          <q-card-section>
            <div class="text-h6">
              {{ $t('agents.edit.title') }}
            </div>
          </q-card-section>

          <q-form @submit.prevent="submitAgentUpdate">
            <q-card-section class="q-gutter-y-md">
              <div>
                <div class="text-caption text-grey-8">
                  {{ $t('scheduled.agent.agentNumber') }}
                </div>

                <q-tel-input
                  v-model="editingAgentData.phoneNumber"
                  :locales="locale"
                  outlined
                  dense
                  :disable=" usedChanges >= maxPhoneNumberChanges"
                />

                <div
                  class="text-caption"
                  :class="usedChanges >= maxPhoneNumberChanges ? 'text-negative' : 'text-grey-7'"
                >
                  {{ $t('agents.list.changesRemaining') }}: {{ usedChanges }} / {{ maxPhoneNumberChanges }}
                </div>
              </div>
            </q-card-section>

            <q-card-actions
              align="right"
            >
              <q-btn
                v-close-popup
                flat
                :label="$t('common.cancel')"
                color="grey-7"
              />
              <q-btn
                type="submit"
                :label="$t('common.save')"
                color="primary"
                :loading="editingAgentLoading"
                :disable="remainingChanges <= 0"
              />
            </q-card-actions>
          </q-form>
        </q-card>
      </q-dialog>
    </div> -->

    <limits-dashboard
      :current="limits.scheduled_messages.current"
      :total="limits.scheduled_messages.total"
      :loading="loadingLimits"
      :progress="progress"
      slider_label="scheduled.limits.usedOfTotal"
    />
    <div class="q-pb-xl q-pt-md">
      <scheduled-list
        :items="messages"
        :loading="loadingMessages"
        :highlight-id="justCreatedId"

        @toggle-active="toggleActive"
        @delete="deleteScheduledMessageAsync"
      />
    </div>

    <q-fab
      color="primary"
      glossy
      icon="mdi-plus"
      direction="up"
      class="fab-create"
      @click="openCreateDialog"
    />

    <scheduled-message-form-dialog
      v-model="showCreateDialog"
      mode="create"
      :full-screen="isMobile"
      title="scheduled.create.title"
      :all-agents="user_agents"
      @saved="onCreateSaved"
    />

    <scheduled-message-form-dialog
      v-model="showEditDialog"
      mode="edit"
      :item-id="editItemId"
      :full-screen="isMobile"
      title="scheduled.edit.title"
      :all-agents="user_agents"
      @saved="onEditSaved"
    />

    <AgentQrDialog
      v-model="qrDialogOpen"
      :agent-name="websiteStore.getAgentName(qrAgent?.id)"
      :qr-url="qrBlobUrl"
      :loading="qrLoading"
    />
  </q-page>
</template>

<style scoped>
.fab-create {
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  z-index: 1;
}

@media (max-width: 768px) {
  .fab-create {
    bottom: 5rem;
  }
}

/* Il tasto "+" passava inosservato, soprattutto da desktop dove la pagina e' larga e vuota.
   Pulsazione lenta + due onde concentriche tipo sasso nell'acqua: si notano senza infastidire.
   Le onde partono dal bordo del bottone e svaniscono espandendosi, sfalsate di 1,2 s.
   Tutto si ferma al passaggio del mouse e rispetta "riduci animazioni". */
.fab-create {
  animation: fab-pulse 2.6s ease-in-out infinite;
}

.fab-create::before,
.fab-create::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: 50%;
  border: 2px solid currentColor;
  color: var(--q-primary);
  opacity: 0;
  animation: fab-ripple 2.4s ease-out infinite;
  pointer-events: none;
}

.fab-create::after {
  animation-delay: 1.2s;
}

.fab-create:hover,
.fab-create:hover::before,
.fab-create:hover::after {
  animation-play-state: paused;
}

@keyframes fab-pulse {
  0%, 100% { transform: scale(1); box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2); }
  50%      { transform: scale(1.06); box-shadow: 0 8px 22px rgba(0, 0, 0, 0.3); }
}

@keyframes fab-ripple {
  0%   { transform: scale(1);   opacity: 0.55; }
  70%  { opacity: 0.12; }
  100% { transform: scale(2.1); opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .fab-create,
  .fab-create::before,
  .fab-create::after { animation: none; }
}
</style>
